<mat-sidenav-container class="rc-page-container">

  <!-- ── Installation status drawer — page-scoped via DrawerService ── -->
  <mat-sidenav #rcDrawer class="software-drawer" position="end" mode="over" (closedStart)="closeDrawer()">

    <div class="drawer-header" *ngIf="drawerPayload">
      <div class="drawer-title">
        <mat-icon class="drawer-title-icon">{{ drawerPayload.titleIcon }}</mat-icon>
        <span>{{ drawerPayload.title }}</span>
      </div>
      <button mat-icon-button (click)="closeDrawer()" class="drawer-close-btn" aria-label="Close drawer">
        <mat-icon>close</mat-icon>
      </button>
    </div>

    <mat-divider></mat-divider>

    <div class="drawer-body" *ngIf="drawerPayload?.content?.length">
      <mat-accordion [multi]="true" class="software-accordion">
        <ng-container
          *ngFor="let categoryGroup of drawerPayload?.content; trackBy: drawerService.trackByCategory.bind(drawerService)">
          <mat-expansion-panel expanded class="software-panel">
            <mat-expansion-panel-header class="software-panel-header">
              <mat-icon class="category-icon">{{ categoryGroup.icon }}</mat-icon>
              <span class="category-name">{{ categoryGroup.category }}</span>
            </mat-expansion-panel-header>

            <div class="category-items">
              <div *ngFor="let item of categoryGroup.items; trackBy: drawerService.trackByItemId.bind(drawerService)"
                class="item-row">
                <mat-icon class="item-icon" [ngClass]="drawerService.getStatusIconClass(item.status)"
                  [matTooltip]="item.error || undefined" matTooltipShowDelay="200">
                  {{ drawerService.getStatusIcon(item.status) }}
                </mat-icon>
                <span class="item-name">{{ item.name }}</span>
                <span class="item-status-badge" [ngClass]="drawerService.getStatusBadgeClass(item.status)"
                  [matTooltip]="item.error || undefined" matTooltipShowDelay="200">
                  {{ item.status }}
                </span>
              </div>
            </div>

          </mat-expansion-panel>
        </ng-container>
      </mat-accordion>
    </div>

  </mat-sidenav>

  <mat-sidenav-content>
    <div class="rc-page">
      <!-- ── Info banner ───────────────────────────────────────────── -->
      <div class="org-banner" *ngIf="bannerVisible">
        <mat-icon class="org-banner-icon">info_outline</mat-icon>
        <div class="org-banner-text">
          <strong>Template Creation</strong>
          <p>
            Templates define the base cloud resources — such as virtual machines and user accounts —
            that can be provisioned for classes and requisitions.
            Templates are organisation-scoped and require dedicated subscriptions to be configured before provisioning.
          </p>
        </div>
        <div class="org-banner-actions">
          <mat-checkbox [(ngModel)]="bannerDismissForever" class="banner-dismiss-check">
            Don't show again
          </mat-checkbox>
          <button mat-icon-button class="banner-close-btn" (click)="dismissBanner()" matTooltip="Dismiss">
            <mat-icon>close</mat-icon>
          </button>
        </div>
      </div>

      <!-- ── Page header ───────────────────────────────────────────── -->
      <div class="rc-header">
        <div class="rc-header-title">
          <mat-icon class="rc-header-icon">grid_view</mat-icon>
          <h2 class="rc-title">Templates</h2>
        </div>
        <button mat-raised-button color="primary" (click)="addTemplate()">
          <mat-icon>add</mat-icon> Add Template
        </button>
      </div>

      <!-- ── Tab group ─────────────────────────────────────────────── -->

      <!-- Page loading spinner -->
      <div *ngIf="isPageLoading" class="rc-page-loading">
        <mat-spinner diameter="40"></mat-spinner>
      </div>

      <!-- Global empty state — no templates at all -->
      <div *ngIf="!isPageLoading && templateTypes.length === 0" class="empty-state rc-global-empty">
        <mat-icon class="empty-state-icon">dashboard_customize</mat-icon>
        <p class="empty-state-title">No templates yet</p>
        <p class="empty-state-sub">Click <strong>+ Add Template</strong> to create a Resource template.</p>
      </div>

      <mat-tab-group *ngIf="templateTypes.length > 0" class="rc-tabs" animationDuration="150ms"
        [selectedIndex]="getActiveTabIndex()" (selectedIndexChange)="setActiveTabByIndex($event)">

        <mat-tab *ngFor="let typeInfo of templateTypes">
          <ng-template mat-tab-label>
            <mat-icon class="rc-tab-icon">{{ typeInfo.icon }}</mat-icon>
            {{ typeInfo.name }}
            <span class="rc-tab-badge">{{ groupedModels.get(typeInfo.type)?.length || 0 }}</span>
          </ng-template>

          <!-- ═══════════════════════════════════════════════════════════
           VM TAB — Material accordion
           ═══════════════════════════════════════════════════════════ -->
          <ng-container *ngIf="typeInfo.type === 'vm'">
            <div *ngIf="getModelsForActiveTab().length > 0; else noTemplates">

              <!-- Expand / Collapse All toolbar -->
              <div class="otp-accordion-toolbar">
                <button mat-stroked-button (click)="expandAllPanels()">
                  <mat-icon>unfold_more</mat-icon> Expand All
                </button>
                <button mat-stroked-button (click)="collapseAllPanels()">
                  <mat-icon>unfold_less</mat-icon> Collapse All
                </button>
              </div>

              <mat-accordion [multi]="true" class="otp-accordion">
                <mat-expansion-panel *ngFor="let model of getModelsForActiveTab()" class="otp-panel">

                  <mat-expansion-panel-header>
                    <mat-panel-title class="otp-panel-title">
                      <mat-icon class="otp-panel-type-icon">description</mat-icon>
                      <span>{{ model.data['TemplateDetailsData']?.templateName ?? model.metaData.name }}</span>
                    </mat-panel-title>
                    <mat-panel-description class="otp-panel-desc">
                      <!-- VM ID — compact monospace pill, shown first when provisioned -->
                      <code *ngIf="model?.cloudIds?.['vmname']"
                        class="otp-pd-vmid">{{ model.cloudIds['vmname'] }}</code>
                      <!-- Status chip -->
                      <span class="vmt-state-chip otp-panel-status-chip"
                        [class.chip-not-started]="model.status === 'draft'"
                        [class.chip-in-progress]="model.status === 'creating'"
                        [class.chip-created]="model.status === 'active'" [class.chip-failed]="model.status === 'failed'"
                        [class.chip-snapshotted]="model.status === 'snapshotted'"
                        [class.chip-pending]="model.status === 'destroyed' || model.status === 'destroy-partial'">
                        <mat-icon class="chip-icon">
                          {{ model.status === 'active' ? 'check_circle' :
                          model.status === 'snapshotted' ? 'verified' :
                          model.status === 'failed' ? 'cancel' :
                          model.status === 'creating' ? 'sync' :
                          model.status === 'destroyed' ? 'delete_outline' : 'cloud_queue' }}
                        </mat-icon>
                        {{ model.status }}
                      </span>
                      <!-- Plain text attrs -->
                      <span *ngIf="getOsName(model)" class="otp-pd-attr">{{ getOsName(model) }}</span>
                      <span *ngIf="getCloudProviderName(model)" class="otp-pd-attr">{{ getCloudProviderName(model)
                        }}</span>
                      <span *ngIf="getRegionName(model)" class="otp-pd-attr">{{ getRegionName(model) }}</span>
                      <span *ngIf="model.data['CloudProviderData']?.size" class="otp-pd-attr"
                        [matTooltip]="getSizeName(model)" matTooltipShowDelay="300">{{
                        model.data['CloudProviderData']?.size
                        }}</span>
                    </mat-panel-description>
                  </mat-expansion-panel-header>

                  <!-- ── Expanded body: 2-col default / 3-col when VM live (grid-areas reorder visually) ── -->
                  <div class="otp-grid" [class.otp-grid-3col]="isVmProvisioned(model) && !isTemplateLoading(model.id)">

                    <!-- ── LEFT: Template Info Card ── -->
                    <app-template-info-card class="vmt-info-card" [data]="buildTemplateInfoCardData(model)"
                      [drawerContent]="getInstallationDrawerContent(model)">
                    </app-template-info-card>

                    <!-- ── RIGHT: VM Controls Card ── -->
                    <mat-card class="vmt-card vmt-action-card">
                      <mat-card-header>
                        <mat-card-title class="vmt-card-title-row">
                          <mat-icon class="vmt-card-title-icon">dns</mat-icon>
                          VM Controls
                          <button *ngIf="canArchive" mat-icon-button color="warn"
                                  style="margin-left: auto"
                                  (click)="archiveTemplate(model)"
                                  matTooltip="Archive this template">
                            <mat-icon>inventory_2</mat-icon>
                          </button>
                        </mat-card-title>
                        <mat-card-subtitle>Provision and manage the org-template virtual machine</mat-card-subtitle>
                      </mat-card-header>

                      <mat-card-content class="vmt-action-content">

                        <!-- In-progress spinner overlay for power actions (start/stop/shutdown) -->
                        <div *ngIf="isActioning(model.id) && !isDeleting(model.id)" class="otp-actioning-banner">
                          <mat-progress-spinner mode="indeterminate" diameter="20"></mat-progress-spinner>
                          <span>{{ getActioningLabel(model.id) }}</span>
                        </div>

                        <!-- No org template subscriptions — blocked state (gates Create VM) -->
                        <div *ngIf="canCreateVm(model) && !hasOrgTemplateSubscriptions"
                          class="vmt-action-body vmt-center">
                          <mat-icon class="vmt-state-icon" style="color: #f57c00;">warning_amber</mat-icon>
                          <p class="vmt-state-title">No template subscription configured</p>
                          <p class="vmt-state-sub">
                            This organisation has no VM template subscription. Add one with available core quota
                            before creating the template VM.
                          </p>
                          <a mat-flat-button color="accent" [routerLink]="['/template-vm-subscriptions']">
                            <mat-icon>dns</mat-icon>
                            Go to Template VM Subscriptions
                          </a>
                        </div>

                        <!-- Create VM — shown only when no VM is provisioned yet and subscriptions exist -->
                        <div *ngIf="canCreateVm(model) && hasOrgTemplateSubscriptions"
                          class="vmt-action-body vmt-center">
                          <mat-icon class="vmt-state-icon">cloud_queue</mat-icon>
                          <p class="vmt-state-title">No VM provisioned yet</p>
                          <p class="vmt-state-sub">Create the template VM to begin testing the org-template environment.
                          </p>
                          <button mat-flat-button color="primary"
                            [disabled]="isTemplateLoading(model.id) || isActioning(model.id)"
                            (click)="provisionOrgTemplateVm(model)">
                            <mat-spinner *ngIf="isTemplateLoading(model.id)" diameter="18"
                              class="btn-spinner"></mat-spinner>
                            <mat-icon *ngIf="!isTemplateLoading(model.id)">add_circle_outline</mat-icon>
                            {{ isTemplateLoading(model.id) ? 'Creating VM… (10–15 min)' : 'Create VM' }}
                          </button>
                        </div>

                        <!-- Creating / provisioning state -->
                        <div *ngIf="isTemplateLoading(model.id) && !canCreateVm(model)"
                          class="vmt-action-body vmt-center">
                          <mat-progress-spinner mode="indeterminate" diameter="36"></mat-progress-spinner>
                          <span class="vmt-state-label">{{ getLoadingStatusText(model) }}</span>
                          <p class="vmt-state-sub">Please wait while the Virtual Machine is being provisioned and
                            selected
                            applications are installed.</p>
                        </div>

                        <!-- Deleting state — shown while the synchronous destroy HTTP call is in-flight -->
                        <div *ngIf="isDeleting(model.id)" class="vmt-action-body vmt-center">
                          <mat-progress-spinner mode="indeterminate" diameter="36"></mat-progress-spinner>
                          <span class="vmt-state-label">Deleting VM…</span>
                          <p class="vmt-state-sub">Azure resources are being removed. This may take a few minutes.</p>
                        </div>

                        <!-- Power control toolbar — shown when VM is provisioned and not being deleted -->
                        <!-- Row 1: session + power  |  Row 2: destructive actions (forced via flex-break) -->
                        <div *ngIf="isVmProvisioned(model) && !isTemplateLoading(model.id) && !isDeleting(model.id)"
                          class="otp-power-toolbar">

                          <button mat-stroked-button color="primary" [disabled]="isActioning(model.id)"
                            (click)="launch(model)" matTooltip="Open a remote desktop session to test this template VM">
                            <mat-icon>play_arrow</mat-icon>
                            Test VM
                          </button>

                          <button mat-stroked-button [disabled]="isActioning(model.id)"
                            (click)="startOrgTemplateVm(model)" matTooltip="Power on the VM (resumes compute charges)">
                            <mat-icon>power_settings_new</mat-icon>
                            Start
                          </button>

                          <button mat-stroked-button color="warn" [disabled]="isActioning(model.id)"
                            (click)="shutdownOrgTemplateVm(model)"
                            matTooltip="Send graceful OS shutdown (VM stays allocated — charges continue)">
                            <mat-icon>stop_circle</mat-icon>
                            Shutdown
                          </button>

                          <!-- Flex row-break: pushes destructive actions to their own row -->
                          <div class="otp-toolbar-row-break"></div>

                          <button mat-stroked-button color="warn" [disabled]="isActioning(model.id)"
                            (click)="stopOrgTemplateVm(model)"
                            matTooltip="Shutdown and deallocate (Azure Portal 'Stop' — charges paused)">
                            <mat-icon>pause_circle</mat-icon>
                            Stop &amp; Deallocate
                          </button>

                          <button mat-stroked-button color="warn" [disabled]="isActioning(model.id)"
                            (click)="deleteOrgTemplate(model)"
                            matTooltip="Permanently destroy the VM and its Azure resources">
                            <mat-icon>delete_forever</mat-icon>
                            Destroy VM
                          </button>

                        </div>


                      </mat-card-content>
                    </mat-card>

                    <!-- ── MIDDLE: Connection Details Card — appears when VM is live (hidden while deleting) ── -->
                    <mat-card *ngIf="isVmProvisioned(model) && !isTemplateLoading(model.id) && !isDeleting(model.id)"
                      class="vmt-card otp-conn-card">
                      <mat-card-header>
                        <mat-card-title class="vmt-card-title-row">
                          <mat-icon class="vmt-card-title-icon">lan</mat-icon>
                          Connection Details
                          <!-- Live power state badge -->
                          <span *ngIf="getPowerState(model.id)" class="otp-power-badge"
                            [class.otp-power-running]="getPowerState(model.id) === 'running'"
                            [class.otp-power-stopped]="getPowerState(model.id) === 'stopped'"
                            [class.otp-power-deallocated]="getPowerState(model.id) === 'deallocated'"
                            [class.otp-power-transitional]="isPowerStateTransitional(model.id)">
                            <mat-icon class="otp-power-badge-icon">
                              {{ getPowerState(model.id) === 'running' ? 'play_circle' :
                              getPowerState(model.id) === 'stopped' ? 'stop_circle' :
                              getPowerState(model.id) === 'deallocated' ? 'pause_circle' : 'sync' }}
                            </mat-icon>
                            {{ getPowerState(model.id) }}
                          </span>
                        </mat-card-title>
                        <mat-card-subtitle>Use these credentials to manually connect to the template VM for
                          troubleshooting</mat-card-subtitle>
                      </mat-card-header>
                      <mat-card-content class="otp-conn-card-content">
                        <div class="otp-conn-grid">

                          <!-- IP Address -->
                          <div class="otp-conn-item" *ngIf="model.cloudIds['publicipaddress']">
                            <span class="vmt-info-label"><mat-icon class="vmt-meta-icon">wifi</mat-icon>Public IP</span>
                            <div class="otp-conn-value-row">
                              <code class="vmt-mono">{{ model.cloudIds['publicipaddress'] }}</code>
                              <button mat-icon-button class="otp-copy-btn"
                                (click)="copyToClipboard(model.cloudIds['publicipaddress'], 'IP address')"
                                matTooltip="Copy IP">
                                <mat-icon>content_copy</mat-icon>
                              </button>
                            </div>
                          </div>

                          <!-- VM Name -->
                          <div class="otp-conn-item" *ngIf="model.cloudIds['vmname']">
                            <span class="vmt-info-label"><mat-icon class="vmt-meta-icon">computer</mat-icon>VM
                              Name</span>
                            <div class="otp-conn-value-row">
                              <code class="vmt-mono">{{ model.cloudIds['vmname'] }}</code>
                              <button mat-icon-button class="otp-copy-btn"
                                (click)="copyToClipboard(model.cloudIds['vmname'], 'VM name')"
                                matTooltip="Copy VM name">
                                <mat-icon>content_copy</mat-icon>
                              </button>
                            </div>
                          </div>

                          <!-- Username -->
                          <div class="otp-conn-item" *ngIf="model.cloudIds['adminusername']">
                            <span class="vmt-info-label"><mat-icon
                                class="vmt-meta-icon">person</mat-icon>Username</span>
                            <div class="otp-conn-value-row">
                              <code class="vmt-mono">{{ model.cloudIds['adminusername'] }}</code>
                              <button mat-icon-button class="otp-copy-btn"
                                (click)="copyToClipboard(model.cloudIds['adminusername'], 'Username')"
                                matTooltip="Copy username">
                                <mat-icon>content_copy</mat-icon>
                              </button>
                            </div>
                          </div>

                          <!-- Password (masked) -->
                          <div class="otp-conn-item" *ngIf="model.cloudIds['adminpassword']">
                            <span class="vmt-info-label"><mat-icon class="vmt-meta-icon">lock</mat-icon>Password</span>
                            <div class="otp-conn-value-row">
                              <code class="vmt-mono otp-password-field">
                      {{ shownPasswords.has(model.id) ? model.cloudIds['adminpassword'] : '••••••••••••' }}
                    </code>
                              <button mat-icon-button class="otp-copy-btn" (click)="togglePasswordVisibility(model.id)"
                                [matTooltip]="shownPasswords.has(model.id) ? 'Hide password' : 'Show password'">
                                <mat-icon>{{ shownPasswords.has(model.id) ? 'visibility_off' : 'visibility'
                                  }}</mat-icon>
                              </button>
                              <button mat-icon-button class="otp-copy-btn"
                                (click)="copyToClipboard(model.cloudIds['adminpassword'], 'Password')"
                                matTooltip="Copy password">
                                <mat-icon>content_copy</mat-icon>
                              </button>
                            </div>
                          </div>
                        </div>

                        <!-- ── VM Connection Template attachment ──
                             Per round-4 feedback: lives INSIDE the Connection
                             Details card as a footer row rather than as a
                             separate section. -->
                        <mat-divider class="otp-conn-template-divider"></mat-divider>
                        <div class="otp-conn-template-inline">
                          <app-vm-connection-template-attach
                            [currentId]="model.vmConnectionTemplateId"
                            [disabled]="!!savingVmConnectionTemplateId.has(model.id)"
                            contextLabel="Applied to launches against this org-level template VM. Detach to use the global server defaults."
                            (change)="onResourceVmConnectionTemplateChange(model, $event)">
                          </app-vm-connection-template-attach>
                        </div>
                      </mat-card-content>
                    </mat-card>

                  </div>



                </mat-expansion-panel>
              </mat-accordion>

            </div>
          </ng-container>

          <!-- ═══════════════════════════════════════════════════════════
           USER ACCOUNT TAB — Accordion with AAD Controls
           ═══════════════════════════════════════════════════════════ -->
          <ng-container *ngIf="typeInfo.type === 'acc'">

            <!-- No UA subscription configured — show navigation CTA -->
            <ng-container *ngIf="!hasUaTemplateSubscriptions; else uaAccordion">
              <div class="vmt-action-body vmt-center" style="padding: 40px 0;">
                <mat-icon class="vmt-state-icon" style="color: #f57c00;">warning_amber</mat-icon>
                <p class="vmt-state-title">No template subscription configured</p>
                <p class="vmt-state-sub">
                  This organisation has no User Account template subscription. Add one before managing
                  AAD users for these templates.
                </p>
                <a mat-flat-button color="accent" [routerLink]="['/template-vm-subscriptions']"
                  [queryParams]="{ tab: 'acc' }">
                  <mat-icon>manage_accounts</mat-icon>
                  Go to Template Account Subscriptions
                </a>
              </div>
            </ng-container>

            <ng-template #uaAccordion>
              <div *ngIf="getModelsForActiveTab().length > 0; else noTemplates">

                <!-- Expand / Collapse All toolbar -->
                <div class="otp-accordion-toolbar">
                  <button mat-stroked-button (click)="expandAllPanels()">
                    <mat-icon>unfold_more</mat-icon> Expand All
                  </button>
                  <button mat-stroked-button (click)="collapseAllPanels()">
                    <mat-icon>unfold_less</mat-icon> Collapse All
                  </button>
                </div>

                <mat-accordion [multi]="true" class="otp-accordion">
                  <mat-expansion-panel *ngFor="let model of getModelsForActiveTab()" class="otp-panel"
                    (opened)="refreshAadStatus(model.id)">

                    <mat-expansion-panel-header>
                      <mat-panel-title class="otp-panel-title">
                        <mat-icon class="otp-panel-type-icon">manage_accounts</mat-icon>
                        <span>{{ model.data['TemplateDetailsData']?.templateName ?? model.metaData.name }}</span>
                      </mat-panel-title>
                      <mat-panel-description class="otp-panel-desc">
                        <!-- AAD status badge -->
                        <span class="ua-aad-badge" [ngClass]="getAadStatusBadgeClass(aadStatus[model.id]?.status)">
                          {{ getAadStatusLabel(aadStatus[model.id]?.status) }}
                        </span>
                        <!-- Provider / Role attributes -->
                        <span *ngIf="getCloudProviderName(model)" class="otp-pd-attr">
                          {{ getCloudProviderName(model) }}
                        </span>
                        <span *ngIf="getAccountRole(model)" class="otp-pd-attr">
                          {{ getAccountRole(model) }}
                        </span>
                      </mat-panel-description>
                    </mat-expansion-panel-header>

                    <!-- Expanded body: 2-col default → 3-col when AAD is Active (matches VM grid pattern) -->
                    <div class="otp-grid" [class.otp-grid-3col]="aadStatus[model.id]?.status === 'Active'">

                      <!-- ── CARD 1: Template Details ── -->
                      <mat-card class="vmt-card vmt-info-card">
                        <mat-card-header>
                          <mat-card-title class="vmt-card-title-row">
                            <mat-icon class="vmt-card-title-icon">description</mat-icon>
                            {{ model.data['TemplateDetailsData']?.templateName ?? model.metaData.name }}
                          </mat-card-title>
                          <mat-card-subtitle>{{ model.data['TemplateDetailsData']?.templateDescription ??
                            model.metaData.description ?? 'Azure AD user account template' }}</mat-card-subtitle>
                        </mat-card-header>

                        <mat-card-content class="vmt-info-content">
                          <div class="vmt-info-grid">
                            <div class="vmt-info-item" *ngIf="getCloudProviderName(model)">
                              <span class="vmt-info-label"><mat-icon
                                  class="vmt-meta-icon">cloud</mat-icon>Provider</span>
                              <span class="vmt-info-value">{{ getCloudProviderName(model) }}</span>
                            </div>
                            <div class="vmt-info-item" *ngIf="getAccountRole(model)">
                              <span class="vmt-info-label"><mat-icon
                                  class="vmt-meta-icon">security</mat-icon>Role</span>
                              <span class="vmt-info-value">{{ getAccountRole(model) }}</span>
                            </div>
                          </div>

                          <!-- AAD state chip — dynamic classes matching VM chip pattern -->
                          <div class="vmt-state-chip-row">
                            <span class="vmt-state-chip"
                              [class.chip-not-started]="!aadStatus[model.id] || aadStatus[model.id]?.status === 'Deleted'"
                              [class.chip-in-progress]="isAadTransitioning(aadStatus[model.id]?.status)"
                              [class.chip-created]="aadStatus[model.id]?.status === 'Active'"
                              [class.chip-failed]="aadStatus[model.id]?.status === 'Failed'">
                              <mat-icon class="chip-icon">
                                {{ aadStatus[model.id]?.status === 'Active' ? 'check_circle' :
                                aadStatus[model.id]?.status === 'Failed' ? 'cancel' :
                                isAadTransitioning(aadStatus[model.id]?.status) ? 'sync' : 'cloud_queue' }}
                              </mat-icon>
                              {{ aadStatus[model.id]?.status ?? 'Not Provisioned' }}
                            </span>
                          </div>
                        </mat-card-content>
                      </mat-card>

                      <!-- ── CARD 2 (middle): Credentials — visible only when Active ── -->
                      <mat-card *ngIf="aadStatus[model.id]?.status === 'Active'" class="vmt-card otp-conn-card">
                        <mat-card-header>
                          <mat-card-title class="vmt-card-title-row">
                            <mat-icon class="vmt-card-title-icon">key</mat-icon>
                            Credentials
                          </mat-card-title>
                          <mat-card-subtitle>Sign in to Azure Portal using these credentials</mat-card-subtitle>
                        </mat-card-header>
                        <mat-card-content class="otp-conn-card-content">
                          <app-ua-credentials-card [templateId]="model.id" [hideClose]="true">
                          </app-ua-credentials-card>
                        </mat-card-content>
                      </mat-card>

                      <!-- ── CARD 3: AAD User Controls ── -->
                      <mat-card class="vmt-card vmt-action-card">
                        <mat-card-header>
                          <mat-card-title class="vmt-card-title-row">
                            <mat-icon class="vmt-card-title-icon">manage_accounts</mat-icon>
                            AAD User Controls
                            <button *ngIf="canArchive" mat-icon-button color="warn"
                                    style="margin-left: auto"
                                    (click)="archiveTemplate(model)"
                                    matTooltip="Archive this template">
                              <mat-icon>inventory_2</mat-icon>
                            </button>
                          </mat-card-title>
                          <mat-card-subtitle>Provision and manage the org-template Azure AD user</mat-card-subtitle>
                        </mat-card-header>

                        <mat-card-content class="vmt-action-content">

                          <!-- Provisioning / Deleting spinner state -->
                          <div *ngIf="isAadTransitioning(aadStatus[model.id]?.status)"
                            class="vmt-action-body vmt-center">
                            <mat-progress-spinner mode="indeterminate" diameter="36"></mat-progress-spinner>
                            <span class="vmt-state-label">
                              {{ aadStatus[model.id]?.status === 'Provisioning'
                              ? 'Provisioning AAD user...'
                              : 'Deleting AAD user...' }}
                            </span>
                            <p class="vmt-state-sub">Please wait while the Azure AD user is being managed.</p>
                          </div>

                          <!-- Idle: no record or Deleted -->
                          <div *ngIf="isAadIdle(aadStatus[model.id]?.status)" class="vmt-action-body vmt-center">
                            <mat-icon class="vmt-state-icon">person_add</mat-icon>
                            <p class="vmt-state-title">No AAD user provisioned</p>
                            <p class="vmt-state-sub">Provision an Azure AD user for this template to grant subscription
                              access.</p>
                            <button mat-flat-button color="primary" (click)="provisionAadUser(model)">
                              <mat-icon>person_add</mat-icon>
                              Provision AAD User
                            </button>
                          </div>

                          <!-- Failed state -->
                          <div *ngIf="aadStatus[model.id]?.status === 'Failed'" class="vmt-action-body vmt-center">
                            <mat-icon class="vmt-state-icon" style="color: #c62828;">error_outline</mat-icon>
                            <p class="vmt-state-title">Provisioning failed</p>
                            <button mat-stroked-button color="primary" (click)="provisionAadUser(model)">
                              <mat-icon>refresh</mat-icon>
                              Retry Provisioning
                            </button>
                          </div>

                          <!-- Active state -->
                          <div *ngIf="aadStatus[model.id]?.status === 'Active'" class="vmt-action-body vmt-center">
                            <div class="otp-power-toolbar" style="justify-content: center; margin-top: 8px;">
                              <button mat-stroked-button [disabled]="regeneratingTap[model.id]" (click)="openAzurePortal()"
                                matTooltip="Open Azure Portal to validate the provisioned user account">
                                <mat-icon>open_in_new</mat-icon>
                                Open Azure Portal
                              </button>
                              <button mat-stroked-button [disabled]="regeneratingTap[model.id]"
                                (click)="regenerateTap(model.id)" matTooltip="Generate a new Temporary Access Pass">
                                <mat-spinner *ngIf="regeneratingTap[model.id]" diameter="16"
                                  class="btn-spinner"></mat-spinner>
                                <mat-icon *ngIf="!regeneratingTap[model.id]">refresh</mat-icon>
                                {{ regeneratingTap[model.id] ? 'Regenerating...' : 'Regenerate TAP' }}
                              </button>
                              <button mat-stroked-button color="warn" [disabled]="regeneratingTap[model.id]"
                                (click)="confirmDeleteAadUser(model)">
                                <mat-icon>person_remove</mat-icon>
                                Delete User
                              </button>
                            </div>
                          </div>

                        </mat-card-content>
                      </mat-card>

                    </div>

                  </mat-expansion-panel>
                </mat-accordion>

              </div>
            </ng-template>

          </ng-container>

          <!-- ═══════════════════════════════════════════════════════════
           OTHER TAB TYPES — fallback flat card grid
           ═══════════════════════════════════════════════════════════ -->
          <ng-container *ngIf="typeInfo.type !== 'vm' && typeInfo.type !== 'acc'">
            <div class="ua-card-grid" *ngIf="getModelsForActiveTab().length > 0; else noTemplates">

              <mat-card *ngFor="let model of getModelsForActiveTab()" class="ua-tpl-card">

                <mat-card-header class="ua-tpl-header">
                  <div mat-card-avatar class="ua-tpl-avatar">
                    <img [src]="getProviderLogoUrl(model)"
                      [alt]="getCloudProviderName(model) || model.data['UserAccountData']?.selectedCloudProvider"
                      class="ua-tpl-avatar-img"
                      onerror="this.style.display='none'; this.nextElementSibling.style.display='flex'" />
                    <mat-icon class="ua-tpl-avatar-fallback" style="display:none">cloud</mat-icon>
                  </div>
                  <mat-card-title class="ua-tpl-name">
                    {{ model.data['TemplateDetailsData']?.templateName ?? model.metaData.name }}
                  </mat-card-title>
                  <mat-card-subtitle class="ua-tpl-desc">
                    {{ model.data['TemplateDetailsData']?.templateDescription ?? model.metaData.description }}
                  </mat-card-subtitle>
                </mat-card-header>

                <mat-card-content class="ua-tpl-content">
                  <div class="ua-tpl-row">
                    <mat-icon class="ua-tpl-row-icon">cloud</mat-icon>
                    <span class="ua-tpl-row-label">Provider</span>
                    <span class="ua-tpl-row-value">
                      {{ getCloudProviderName(model) || (model.data['UserAccountData']?.selectedCloudProvider |
                      uppercase)
                      }}
                    </span>
                  </div>
                  <div class="ua-tpl-row" *ngIf="getAccountRole(model)">
                    <mat-icon class="ua-tpl-row-icon">verified_user</mat-icon>
                    <span class="ua-tpl-row-label">Role</span>
                    <span class="ua-tpl-row-value">{{ getAccountRole(model) }}</span>
                  </div>
                </mat-card-content>

              </mat-card>

            </div>
          </ng-container>

          <ng-template #noTemplates>
            <div class="empty-state">
              <mat-icon class="empty-state-icon">desktop_windows</mat-icon>
              <p class="empty-state-title">No templates yet</p>
              <p class="empty-state-sub">Click <strong>+ Add Template</strong> to create your first template.</p>
            </div>
          </ng-template>

        </mat-tab>
      </mat-tab-group>

    </div><!-- /rc-page -->
  </mat-sidenav-content>

</mat-sidenav-container>
